import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { usePositionStore } from '../stores/positionStore';
import { useCopyTradeStore } from '../stores/copyTradeStore';
import socketManager from '../lib/socket';
import api from '../lib/api';
import EngineWarningsPanel from '../components/EngineWarningsPanel';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  Target,
  Activity,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  ExternalLink,
  Zap,
  Sparkles,
  Users,
  Bell
} from 'lucide-react';

function StatCard({ title, value, change, icon: Icon, trend, gradient }) {
  return (
    <div className="card card-hover group">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-dark-400 font-medium">{title}</p>
          <p className="text-3xl font-bold mt-2">{value}</p>
          {change !== undefined && (
            <p className={`text-sm mt-2 flex items-center gap-1 ${
              trend === 'up' ? 'text-emerald-400' : trend === 'down' ? 'text-red-400' : 'text-dark-400'
            }`}>
              {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : 
               trend === 'down' ? <ArrowDownRight className="w-4 h-4" /> : null}
              {change}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl transition-all duration-300 ${
          gradient 
            ? 'bg-gradient-to-br from-primary-600/20 to-accent-600/20 group-hover:from-primary-600/30 group-hover:to-accent-600/30' 
            : 'bg-dark-700/50 group-hover:bg-dark-700'
        }`}>
          <Icon className={`w-6 h-6 ${gradient ? 'text-primary-400' : 'text-dark-400 group-hover:text-primary-400'} transition-colors`} />
        </div>
      </div>
    </div>
  );
}

function RecentTrade({ trade }) {
  const isBuy = trade.side === 'BUY';
  const isPending = trade.status === 'pending' || trade.status === 'submitted';
  const isFailed = trade.status === 'failed' || trade.status === 'cancelled' || trade.status === 'rejected';
  
  // Use filledSize/avgFillPrice if available, otherwise fall back to requested values
  const shares = trade.filledSize > 0 ? trade.filledSize : (trade.requestedSize || 0);
  const price = trade.avgFillPrice > 0 ? trade.avgFillPrice : (trade.requestedPrice || 0);
  const notional = trade.totalValue > 0 ? trade.totalValue : (shares * price);
  const notionalDisplay = Number.isFinite(notional) && notional > 0 ? notional.toFixed(2) : '—';
  
  // Format shares and price display
  const sharesDisplay = shares > 0 ? shares.toFixed(2) : '—';
  const priceDisplay = price > 0 ? `$${price.toFixed(2)}` : '—';

  // Status indicator color
  const statusColor = isFailed ? 'bg-red-500' : isPending ? 'bg-amber-400 animate-pulse' : (isBuy ? 'bg-emerald-400' : 'bg-red-400');
  const textColor = isFailed ? 'text-red-400' : isPending ? 'text-amber-400' : (isBuy ? 'text-emerald-400' : 'text-red-400');

  return (
    <div className="flex items-center justify-between py-3 border-b border-dark-700 last:border-0">
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${statusColor}`} />
        <div>
          <p className="font-medium text-sm truncate max-w-[200px]">
            {trade.market?.question || 'Unknown Market'}
          </p>
          <p className="text-xs text-dark-400">
            {trade.side} • {sharesDisplay} shares @ {priceDisplay}
            {isPending && <span className="ml-1 text-amber-400">(pending)</span>}
            {isFailed && <span className="ml-1 text-red-400">(failed)</span>}
          </p>
        </div>
      </div>
      <div className="text-right">
        <p className={`text-sm font-medium ${textColor}`}>
          {isPending ? '⏳' : isFailed ? '✗' : (isBuy ? '+' : '-')}${notionalDisplay}
        </p>
        <p className="text-xs text-dark-400">
          {new Date(trade.createdAt).toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
}

function ActivePosition({ position }) {
  const pnlPercent = position.unrealizedPnLPercent || 0;
  const isProfit = pnlPercent >= 0;
  
  return (
    <div className="flex items-center justify-between py-3 border-b border-dark-700 last:border-0">
      <div className="flex-1 min-w-0">
        <p className="font-medium text-sm truncate">
          {position.market?.question || 'Unknown Market'}
        </p>
        <p className="text-xs text-dark-400">
          {position.size?.toFixed(2)} shares • Avg: ${position.avgPrice?.toFixed(2)}
        </p>
      </div>
      <div className="text-right ml-4">
        <p className={`text-sm font-medium ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
          {isProfit ? '+' : ''}{pnlPercent.toFixed(1)}%
        </p>
        <p className="text-xs text-dark-400">
          ${position.currentValue?.toFixed(2)}
        </p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, initialize } = useAuthStore();
  const {
    positions,
    summary,
    trades,
    fetchPositions,
    fetchSummary,
    fetchTrades,
    updatePosition,
    addTrade
  } = usePositionStore();
  const { configs, fetchConfigs } = useCopyTradeStore();
  const [notifications, setNotifications] = useState([]);
  const [liveBalance, setLiveBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // Fetch live wallet balance from CLOB
  const fetchLiveBalance = useCallback(async () => {
    if (!user?.wallet?.address) return;
    setBalanceLoading(true);
    try {
      const { data } = await api.get('/wallet/balance');
      const balance = data?.balanceUsd ?? data?.balance ?? null;
      if (balance !== null) {
        setLiveBalance(parseFloat(balance));
      }
    } catch (err) {
      console.log('Could not fetch live balance:', err.message);
    } finally {
      setBalanceLoading(false);
    }
  }, [user?.wallet?.address]);

  useEffect(() => {
    fetchPositions();
    fetchSummary();
    fetchConfigs();
    // Load recent trades history for this user
    fetchTrades({ limit: 10 });
    // Refresh user data to get latest wallet balance
    initialize();
    // Fetch live balance from CLOB
    fetchLiveBalance();
    
    // Subscribe to real-time updates
    if (!user?._id) return;

    socketManager.subscribeToPositions(user._id);

    // Debounced refresh from server - prevents UI flicker from rapid socket events
    let refreshTimeout = null;
    const debouncedRefresh = () => {
      if (refreshTimeout) clearTimeout(refreshTimeout);
      refreshTimeout = setTimeout(() => {
        fetchPositions('open', false); // silent refresh
        fetchSummary();
        fetchTrades({ limit: 10 });
      }, 5000); // Wait 5s after last event
    };

    const onPositionUpdated = (position) => {
      updatePosition(position);
      debouncedRefresh(); // Debounced refresh to prevent flicker
      if (position && position.status && position.status !== 'open') {
        const status = position.status;
        let title = 'Position updated';
        let severity = 'info';
        if (status === 'closed') {
          title = 'Position closed';
        } else if (status === 'take_profit') {
          title = 'Take profit hit';
          severity = 'success';
        } else if (status === 'stopped_out') {
          title = 'Stop loss hit';
          severity = 'warning';
        } else if (status === 'market_resolved') {
          title = 'Market resolved';
        } else if (status === 'expired' || status === 'liquidated') {
          title = 'Position exited';
          severity = 'warning';
        }

        const question = position.market?.question || 'Unknown market';
        const realized = typeof position.realizedPnL === 'number' ? position.realizedPnL : null;
        if (realized !== null && realized < 0) {
          severity = severity === 'success' ? 'warning' : severity;
        }

        setNotifications((prev) => [
          {
            id: `pos-${position._id}-${status}-${Date.now()}`,
            type: 'position',
            severity,
            title,
            message: question,
            createdAt: new Date().toISOString()
          },
          ...prev
        ].slice(0, 50));
      }
    };

    const onTradeExecuted = (trade) => {
      debouncedRefresh(); // Debounced refresh when trade executes
      const side = (trade.side || 'BUY').toUpperCase();
      const isBuy = side === 'BUY';
      const question = trade.market?.question || 'Unknown market';
      const size = typeof trade.filledSize === 'number' ? trade.filledSize.toFixed(2) : null;
      const price = typeof trade.avgFillPrice === 'number' ? trade.avgFillPrice.toFixed(3) : null;
      const createdAt = trade.createdAt || new Date().toISOString();

      setNotifications((prev) => [
        {
          id: `trade-ok-${trade._id || `${Date.now()}`}`,
          type: 'trade_executed',
          severity: 'success',
          title: isBuy ? 'Buy filled' : 'Sell filled',
          message:
            size && price
              ? `${side} ${size} @ $${price}  ${question}`
              : `${side}  ${question}`,
          createdAt
        },
        ...prev
      ].slice(0, 50));
    };

    const onTradeFailed = (trade) => {
      debouncedRefresh(); // Debounced refresh after failed trade
      const side = (trade.side || 'BUY').toUpperCase();
      const question = trade.market?.question || 'Unknown market';
      const createdAt = trade.createdAt || new Date().toISOString();
      const errorMessage = trade.errorMessage || 'Order rejected or not filled';

      setNotifications((prev) => [
        {
          id: `trade-fail-${trade._id || `${Date.now()}`}`,
          type: 'trade_failed',
          severity: 'error',
          title: 'Trade failed',
          message: `${side}  ${question}  ${errorMessage}`,
          createdAt
        },
        ...prev
      ].slice(0, 50));
    };

    const onNotification = (n) => {
      if (!n) return;
      setNotifications((prev) => [
        {
          id: n.id || `server-${n.type || 'notification'}-${Date.now()}`,
          type: n.type || 'notification',
          severity: n.severity || 'info',
          title: n.title || 'Notification',
          message: n.message || '',
          createdAt: n.createdAt || new Date().toISOString()
        },
        ...prev
      ].slice(0, 50));
    };

    socketManager.on('positionUpdated', onPositionUpdated);
    socketManager.on('tradeExecuted', onTradeExecuted);
    socketManager.on('tradeFailed', onTradeFailed);
    socketManager.on('notification', onNotification);

    return () => {
      if (refreshTimeout) clearTimeout(refreshTimeout);
      socketManager.off('positionUpdated', onPositionUpdated);
      socketManager.off('tradeExecuted', onTradeExecuted);
      socketManager.off('tradeFailed', onTradeFailed);
      socketManager.off('notification', onNotification);
    };
  }, [user?._id]);

  const activeConfigs = configs.filter(c => c.isActive);
  const openPositions = positions.filter(p => p.status === 'open');

  const totalValue = summary?.totalValue || 0;
  const totalUnrealizedPnL = summary?.totalUnrealizedPnL || 0;
  const unrealizedPercent = totalValue > 0
    ? (totalUnrealizedPnL / totalValue) * 100
    : 0;
  const unrealizedTrend = totalUnrealizedPnL >= 0 ? 'up' : 'down';

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Engine Warnings - Important alerts for users */}
      <EngineWarningsPanel maxVisible={5} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <span className="text-gradient">Dashboard</span>
            <Sparkles className="w-6 h-6 text-accent-400" />
          </h1>
          <p className="text-dark-400 mt-1">Your copy trading command center</p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://discord.gg/dFSefc9Hs9"
            target="_blank"
            rel="noreferrer"
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800/80 border border-dark-600 text-xs text-dark-200 hover:border-primary-500 hover:text-white transition-colors"
          >
            <Users className="w-4 h-4 text-primary-400" />
            <span>Discord support</span>
          </a>
          {!user?.wallet?.isConnected && (
            <Link
              to="/settings"
              className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-amber-500/20 to-orange-500/20 text-amber-400 rounded-xl border border-amber-500/20 hover:from-amber-500/30 hover:to-orange-500/30 transition-all"
            >
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Connect Wallet to Start</span>
            </Link>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        <StatCard
          title="Wallet Balance"
          value={balanceLoading ? '...' : `$${(liveBalance ?? user?.wallet?.balance ?? 0).toFixed(2)}`}
          change={liveBalance !== null ? 'Live • USDC.e' : 'USDC.e'}
          icon={Wallet}
          gradient
        />
        <StatCard
          title="Portfolio Value"
          value={`$${(summary?.totalValue || 0).toFixed(2)}`}
          icon={TrendingUp}
        />
        <StatCard
          title="Unrealized P&L"
          value={`$${(summary?.totalUnrealizedPnL || 0).toFixed(2)}`}
          change={totalValue > 0 ? `${unrealizedPercent.toFixed(1)}%` : '—'}
          trend={totalValue > 0 ? unrealizedTrend : undefined}
          icon={totalUnrealizedPnL >= 0 ? TrendingUp : TrendingDown}
        />
        <StatCard
          title="Active Configs"
          value={activeConfigs.length}
          change={`${configs.length} total`}
          icon={Target}
        />
        <StatCard
          title="Open Positions"
          value={openPositions.length}
          icon={Activity}
        />
      </div>

      {/* Notifications */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-dark-800">
              <Bell className="w-4 h-4 text-primary-400" />
            </span>
            <div>
              <h2 className="text-sm font-semibold">Notifications</h2>
              <p className="text-[0.7rem] text-dark-500">
                Live trade and position events for this wallet
              </p>
            </div>
          </div>
          {notifications.length > 0 && (
            <span className="text-[0.7rem] text-dark-500">
              {notifications.length > 9 ? '9+' : notifications.length}
            </span>
          )}
        </div>
        {notifications.length === 0 ? (
          <p className="text-xs text-dark-500">
            No recent events yet. New trades, closes, and errors will show up here.
          </p>
        ) : (
          <div className="space-y-2 max-h-56 overflow-y-auto pr-1 text-xs">
            {notifications.map((n) => (
              <div
                key={n.id}
                className="flex items-start justify-between gap-2 py-1.5 border-b border-dark-800 last:border-0"
              >
                <div className="flex items-start gap-2">
                  <span
                    className={`mt-1 h-1.5 w-1.5 rounded-full ${
                      n.severity === 'success'
                        ? 'bg-emerald-400'
                        : n.severity === 'error'
                        ? 'bg-red-400'
                        : n.severity === 'warning'
                        ? 'bg-amber-400'
                        : 'bg-primary-400'
                    }`}
                  />
                  <div>
                    <p className="text-[0.75rem] font-medium">{n.title}</p>
                    {n.message && (
                      <p className="text-[0.7rem] text-dark-400 line-clamp-2">{n.message}</p>
                    )}
                  </div>
                </div>
                {n.createdAt && (
                  <span className="text-[0.65rem] text-dark-500 whitespace-nowrap">
                    {new Date(n.createdAt).toLocaleTimeString()}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Positions */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Active Positions</h2>
            <Link to="/positions" className="text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1">
              View all <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          
          {openPositions.length > 0 ? (
            <div className="divide-y divide-dark-700/50">
              {openPositions.slice(0, 5).map(position => (
                <ActivePosition key={position._id} position={position} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary-600/20 to-accent-600/20 flex items-center justify-center">
                <Activity className="w-8 h-8 text-primary-400" />
              </div>
              <p className="text-dark-300 font-medium mb-1">No active positions</p>
              <p className="text-dark-500 text-sm mb-4">Start by copying a trader</p>
              <Link to="/traders" className="btn btn-primary text-sm px-4 py-2">
                <Zap className="w-4 h-4 mr-1.5 inline" />
                Find Traders
              </Link>
            </div>
          )}
        </div>

        {/* Recent Trades */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold">Recent Trades</h2>
            <Link to="/positions" className="text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1">
              View all <ExternalLink className="w-3 h-3" />
            </Link>
          </div>
          
          {trades.length > 0 ? (
            <div className="divide-y divide-dark-700/50">
              {trades.slice(0, 5).map((trade, i) => (
                <RecentTrade key={trade._id || i} trade={trade} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-emerald-600/20 to-cyan-600/20 flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-emerald-400" />
              </div>
              <p className="text-dark-300 font-medium mb-1">No recent trades</p>
              <p className="text-dark-500 text-sm">Trades will appear here in real-time</p>
            </div>
          )}
        </div>
      </div>

      {/* Active Copy Configs */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">Active Copy Configurations</h2>
          <Link to="/copy-configs" className="text-sm text-primary-400 hover:text-primary-300 flex items-center gap-1">
            Manage <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
        
        {activeConfigs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Trader</th>
                  <th>Mode</th>
                  <th>Max Position</th>
                  <th>Stop Loss</th>
                  <th>Trades</th>
                  <th>P&L</th>
                </tr>
              </thead>
              <tbody>
                {activeConfigs.map(config => (
                  <tr key={config._id}>
                    <td>
                      <div>
                        <p className="font-medium">{config.traderName}</p>
                        <p className="text-xs text-dark-400 font-mono">
                          {config.traderAddress?.slice(0, 8)}...
                        </p>
                      </div>
                    </td>
                    <td className="capitalize">{config.sizing?.mode}</td>
                    <td>${config.sizing?.maxPositionSize}</td>
                    <td>
                      {config.riskManagement?.stopLoss?.enabled ? (
                        <span className="badge badge-warning">
                          {config.riskManagement.stopLoss.percentage || config.riskManagement.stopLoss.value}%
                        </span>
                      ) : (
                        <span className="text-dark-500">Off</span>
                      )}
                    </td>
                    <td>{config.stats?.totalCopiedTrades || 0}</td>
                    <td className={config.stats?.totalPnL >= 0 ? 'pnl-positive' : 'pnl-negative'}>
                      ${(config.stats?.totalPnL || 0).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-primary-600/20 to-accent-600/20 flex items-center justify-center">
              <Target className="w-8 h-8 text-primary-400" />
            </div>
            <p className="text-dark-300 font-medium mb-1">No active copy configurations</p>
            <p className="text-dark-500 text-sm mb-4">Set up your first copy trading config</p>
            <Link to="/traders" className="btn btn-gradient text-sm px-5 py-2.5">
              <Zap className="w-4 h-4 mr-1.5 inline" />
              Find Traders to Copy
            </Link>
          </div>
        )}
      </div>

      {/* Copy Trading Blog / Education Section for SEO */}
      {false && (
        <div className="card">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Copy Trading Polymarket with BetMimic</h2>
            <span className="text-xs text-dark-500 uppercase tracking-wide hidden sm:inline">
              Learn • Automate • Scale
            </span>
          </div>

          <p className="text-sm text-dark-400 mb-4">
            BetMimic connects directly to the official Polymarket CLOB and turns high-conviction traders
            into an automated signal stream for your own wallet. Instead of manually chasing markets and
            screenshots, you define copy configs, risk limits and sizing rules, and the engine mirrors
            every eligible trade with real on-chain orders, live PnL and position tracking.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            <article className="space-y-2">
              <h3 className="text-sm font-semibold">Why copy trade Polymarket?</h3>
              <p className="text-xs text-dark-400">
                Polymarket is where sharp prediction markets live: politics, sports, macro and memes priced
                in real-time. Copy trading lets you plug into that edge without needing to sit on the
                order book all day.
              </p>
              <ul className="text-xs text-dark-400 list-disc list-inside space-y-1">
                <li>Mirror proven traders across all Polymarket markets automatically.</li>
                <li>Benefit from their research, entry timing and position sizing.</li>
                <li>Stay fully in control of your own wallet, limits and exposure.</li>
              </ul>
            </article>

            <article className="space-y-2">
              <h3 className="text-sm font-semibold">How BetMimic keeps P&amp;L real</h3>
              <p className="text-xs text-dark-400">
                The engine reads trades and positions directly from Polymarket, not estimates. Every copy
                trade is placed with the official client, and open positions are reconciled against the
                Polymarket Data API so your Dashboard always reflects real P&amp;L.
              </p>
              <ul className="text-xs text-dark-400 list-disc list-inside space-y-1">
                <li>Live USDC.e balance and allowance checks before copies fire.</li>
                <li>Open/closed positions synced with Polymarket users/positions endpoints.</li>
                <li>Auto-redeem and ghost-position cleanup to keep history clean.</li>
              </ul>
            </article>

            <article className="space-y-2">
              <h3 className="text-sm font-semibold">Build a strategy, not just vibes</h3>
              <p className="text-xs text-dark-400">
                Copy trading on BetMimic is still a strategy game. You choose who to follow, how much to
                risk per trade, and when to stop. The platform gives you the rails to run it like a
                professional Polymarket book.
              </p>
              <ul className="text-xs text-dark-400 list-disc list-inside space-y-1">
                <li>Per-config max exposure, daily loss limits and trade caps.</li>
                <li>Stop loss / take profit levels enforced by the backend monitor.</li>
                <li>Full trade and position history to review every outcome.</li>
              </ul>
            </article>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-dark-500">
            <span>Want to go deeper?</span>
            <Link to="/tutorial" className="text-primary-400 hover:text-primary-300 font-medium">
              Read the full BetMimic tutorial
            </Link>
            <span className="hidden sm:inline">·</span>
            <Link to="/traders" className="text-primary-400 hover:text-primary-300 font-medium">
              Browse traders to copy on Polymarket
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
